import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mockSegments } from '../../shared/data/segments';
import { Sparkles, ArrowLeft, CheckCircle2, HelpCircle, Monitor, Cpu } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';

export const SegmentOverview: React.FC = () => {
  const { segmentSlug } = useParams<{ segmentSlug: string }>();
  const segment = mockSegments[segmentSlug || 'imobiliarias'] || mockSegments['imobiliarias'];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [segmentSlug]);

  const handleWhatsAppInquiry = (planName?: string) => {
    const msg = encodeURIComponent(`Olá, vi o modelo da YouMachine para o segmento ${segment.name} (${planName || 'Geral'}) e gostaria de solicitar um diagnóstico comercial.`);
    window.open(`https://wa.me/554988416296?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-slate-100 font-sans selection:bg-teal-500 selection:text-black">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/solucoes" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Voltar para Soluções
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-teal-400 font-bold uppercase tracking-widest hidden sm:inline">Blueprint: {segment.name}</span>
            <button 
              onClick={() => handleWhatsAppInquiry()}
              className="px-5 py-2.5 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors"
            >
              Solicitar Diagnóstico
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 relative overflow-hidden border-b border-white/10">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-teal-400 mb-6 text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-4 h-4" />
            Solução 360º — {segment.name}
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-tight mb-6">
            {segment.tagline}
          </h1>
          <p className="text-lg text-slate-300 font-light max-w-2xl mx-auto leading-relaxed mb-10">
            {segment.description}
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {segment.slug === 'imobiliarias' && (
              <Link to="/turquesa-fitness/imobiliaria" target="_blank" className="px-6 py-3 rounded-xl bg-emerald-500 text-slate-950 font-bold text-xs uppercase tracking-wider hover:opacity-90 transition-opacity">
                📄 Testar Demo Imobiliária
              </Link>
            )}
            {segment.slug === 'advocacia' && (
              <Link to="/turquesa-fitness/advocacia" target="_blank" className="px-6 py-3 rounded-xl bg-indigo-500 text-white font-bold text-xs uppercase tracking-wider hover:opacity-90 transition-opacity">
                📄 Testar Demo Advocacia
              </Link>
            )}
            <button onClick={() => handleWhatsAppInquiry()} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition-colors">
              Falar com Consultor
            </button>
          </div>
        </div>
      </section>

      {/* Journey 360º */}
      <section className="py-20 px-6 max-w-7xl mx-auto border-b border-white/10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="emerald" className="mb-3">JORNADA 360º DO CLIENTE</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Como a Estrutura se Conecta</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {segment.journey_steps.map((step, idx) => (
            <Card key={idx} className="bg-[#111111] border-white/10 p-6 relative">
              <div className="text-3xl font-black text-teal-500/30 mb-4">0{idx + 1}</div>
              <h3 className="font-bold text-white text-lg mb-2">{step.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed font-light">{step.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Diagnostic Questions Section */}
      <section className="py-20 px-6 max-w-5xl mx-auto border-b border-white/10">
        <div className="bg-[#111111] rounded-3xl p-8 sm:p-12 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <HelpCircle className="w-8 h-8 text-amber-400" />
            <div>
              <h3 className="text-2xl font-bold text-white">Perguntas para Personalizar esta Solução</h3>
              <p className="text-xs text-slate-400">Em uma conversa de diagnóstico, avaliaremos estes pontos essenciais com sua equipe</p>
            </div>
          </div>

          <ul className="space-y-4">
            {segment.diagnostic_questions.map((q, idx) => (
              <li key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/5">
                <Badge variant="amber" className="shrink-0 mt-0.5">PERGUNTA 0{idx + 1}</Badge>
                <p className="text-sm text-slate-200 font-light leading-relaxed">{q}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Solutions Breakdown */}
      <section className="py-20 px-6 max-w-7xl mx-auto border-b border-white/10">
        <h2 className="text-3xl font-bold text-white mb-12 text-center">Componentes da Solução</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card className="bg-[#111111] border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Monitor className="w-6 h-6 text-teal-400" />
              <h3 className="text-xl font-bold text-white">Site & Vitrine Virtual</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {segment.features.site.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                  <span className="font-light">{f}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="bg-[#111111] border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <Cpu className="w-6 h-6 text-indigo-400" />
              <h3 className="text-xl font-bold text-white">Sistema de Gestão & CRM</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {segment.features.system.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" />
                  <span className="font-light">{f}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-white mb-12 text-center">Planos e Investimento Canônico</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {segment.pricing_plans.map((plan, idx) => (
            <Card key={idx} className={`p-8 border ${idx === 1 ? 'border-teal-500 bg-teal-950/20' : 'border-white/10 bg-[#111111]'}`}>
              <Badge variant={idx === 1 ? 'emerald' : 'slate'} className="mb-4">{plan.name}</Badge>
              <div className="mb-6">
                <span className="text-xs text-slate-400 block">Implantação inicial:</span>
                <span className="text-3xl font-black text-white">R$ {plan.setup_price.toLocaleString('pt-BR')}</span>
                <span className="text-xs text-slate-400 block mt-2">Manutenção / Gestão mensal:</span>
                <span className="text-xl font-bold text-teal-400">R$ {plan.monthly_price.toLocaleString('pt-BR')} / mês</span>
                {plan.ad_budget && <p className="text-xs text-amber-300 mt-2">{plan.ad_budget}</p>}
              </div>

              <ul className="space-y-3 text-xs text-slate-300 mb-8">
                {plan.features.map((feat, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleWhatsAppInquiry(plan.name)}
                className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs uppercase tracking-wider transition-colors"
              >
                Solicitar Diagnóstico
              </button>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};
